import {
  Overlay,
  OverlayOutsideClickDispatcher,
  OverlayRef,
  ViewportRuler,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Injectable,
  Injector,
  signal,
  Type,
} from '@angular/core';
import { AnalyticsService } from '@core/services';
import {
  ConfirmDialogData,
  MibBreakpointService,
  MibConfirmDialogComponent,
} from '@medinbox/angular';
import {
  AnalyticsEvents,
  CASE_LIBRARY_SEARCH_CHAT_ID,
  HitlMessage,
  MessageRole,
  PromptExample,
} from '@shared/models';
import { filter, switchMap, takeUntil } from 'rxjs';
import { CREATE_VIDEO_CUT_PROMPT_TITLE } from './prompt-exemple.service';
import { Dialog } from '@angular/cdk/dialog';
import { ChatInfo } from './chat';
import { ChatConversations, Conversation } from './chat-conversations';

/**
 * One chat on the page, and the conversation it shows. Provided by the screen holding the chat, so
 * two chats mounted at once each read their own; the conversations themselves are shared.
 */
@Injectable()
export class ChatService {
  private readonly breakpointService = inject(MibBreakpointService);
  private readonly conversations = inject(ChatConversations);
  private readonly dialog = inject(Dialog);
  private readonly analytics = inject(AnalyticsService);
  private readonly injector = inject(Injector);

  private readonly overlay = inject(Overlay);
  private readonly outsideClickDispatcher = inject(OverlayOutsideClickDispatcher);
  private readonly viewportRuler = inject(ViewportRuler);

  private readonly shown = signal<Conversation | undefined>(undefined);

  protected readonly _promptBox = signal<{ setPrompt(prompt: string): void } | null>(null);
  protected readonly _promptExampleOverlay = signal<OverlayRef | null>(null);
  protected readonly _promptExamplesDisplayed = signal(false);

  readonly hasMessages = computed(() => (this.shown()?.messages().length ?? 0) > 0);
  readonly filteredMessages = computed(() =>
    (this.shown()?.messages() ?? []).filter((msg) => msg.role !== MessageRole.System),
  );
  readonly assistantIntro = computed(() => this.shown()?.assistantIntro() ?? '');
  readonly assistantSubtext = computed(() => this.shown()?.assistantSubtext() ?? '');
  readonly promptExamples = computed(() => {
    const shown = this.shown();
    if (!shown) {
      return [];
    }
    return shown.hideVideoCutPrompt()
      ? shown.promptExamples().filter((example) => example.title !== CREATE_VIDEO_CUT_PROMPT_TITLE)
      : shown.promptExamples();
  });
  readonly chatLocked = computed(() => this.shown()?.chatLocked() ?? true);
  readonly hitlMessage = computed(() => this.shown()?.hitlMessage() ?? null);
  readonly isThinking = computed(() => this.shown()?.isThinking() ?? false);
  readonly draft = computed(() => this.shown()?.draft() ?? '');
  /** The chat's id, which keys what the screen keeps of it between two visits. */
  readonly chatId = computed(() => this.shown()?.chatId);

  keepDraft(text: string): void {
    this.shown()?.draft.set(text);
  }

  private get currentChatId(): string | undefined {
    return this.shown()?.chatId;
  }

  /** Shows a chat's conversation, fetched the first time any chat asks for it. */
  initChat(chatInfo: ChatInfo, destroyRef: DestroyRef): void {
    destroyRef.onDestroy(() => this._promptExamplesDisplayed.set(false));
    this.shown.set(this.conversations.open(chatInfo));
  }

  refreshMessageList(): void {
    const shown = this.shown();
    if (shown) {
      this.conversations.refresh(shown);
    }
  }

  sendMessage(prompt: string, hitlApproval?: boolean): void {
    const conversation = this.shown();
    const videoId = conversation?.chatId;
    let hitl: { message: HitlMessage; approved: boolean } | undefined;
    const hitlMessage = conversation?.hitlMessage();

    if (hitlMessage) {
      hitl = {
        message: hitlMessage,
        approved: typeof hitlApproval === 'boolean' ? hitlApproval : false,
      };
    } else {
      const event =
        videoId === CASE_LIBRARY_SEARCH_CHAT_ID
          ? AnalyticsEvents.LibraryChatMessageSent
          : AnalyticsEvents.CaseChatMessageSent;
      this.analytics.trackEvent(event, { case_id: videoId });
    }

    if (conversation && (prompt || hitl)) {
      this.conversations.send(conversation, prompt, hitl);
    }
  }

  openPromptExampleDropdown(
    origin: ElementRef,
    promptBox: { setPrompt(prompt: string): void },
    dropdownComponent: Type<object>,
  ): void {
    const overlayRef =
      this.breakpointService.sizeClass() !== 'expanded'
        ? this.createMobileOverlay()
        : this.createDesktopOverlay(origin);

    // This chat's injector: the dropdown lists the examples of this chat, not of another one.
    overlayRef.attach(new ComponentPortal(dropdownComponent, null, this.injector));
    this._promptExamplesDisplayed.set(true);

    // automatically close the overlay on viewport resize
    this.viewportRuler
      .change()
      .pipe(takeUntil(overlayRef.detachments()))
      .subscribe(() => this.closeOverlay(overlayRef));
    // close overlay on backdrop click
    overlayRef.backdropClick().subscribe(() => this.closeOverlay(overlayRef));

    // update local state so we can easily manage global layout, prompt selection and overlay closing afterward
    this._promptExampleOverlay.set(overlayRef);
    this._promptBox.set(promptBox);
  }

  selectPromptExample(example: PromptExample): void {
    const promptBox = this._promptBox();
    const overlayRef = this._promptExampleOverlay();
    if (overlayRef) {
      this.closeOverlay(overlayRef);
    }
    if (promptBox) {
      const prompt = example.prompt;
      const event =
        this.currentChatId === CASE_LIBRARY_SEARCH_CHAT_ID
          ? AnalyticsEvents.LibraryChatShortcutSent
          : AnalyticsEvents.CaseChatShortcutSent;
      this.analytics.trackEvent(event, {
        case_id: this.currentChatId,
        selected_shortcut: prompt,
      });
      promptBox.setPrompt(prompt);
    }
  }

  clearChatHistory(): void {
    const conversation = this.shown();
    if (!conversation) return;

    const dialogRef = this.dialog.open<boolean, ConfirmDialogData>(MibConfirmDialogComponent, {
      data: {
        title: 'Clear conversation?',
        description:
          'This will permanently delete all messages in this conversation. This action cannot be undone.',
        theme: 'danger',
        confirmLabel: 'Remove',
      },
    });
    dialogRef.closed
      .pipe(
        filter((confirmed) => !!confirmed),
        switchMap(() => {
          const event =
            conversation.chatId === CASE_LIBRARY_SEARCH_CHAT_ID
              ? AnalyticsEvents.LibraryChatHistoryCleared
              : AnalyticsEvents.CaseChatHistoryCleared;
          this.analytics.trackEvent(event, {
            case_id: conversation.chatId,
          });
          return this.conversations.clear(conversation);
        }),
      )
      .subscribe();
  }

  private closeOverlay(overlayRef: OverlayRef): void {
    const rootContainer = overlayRef.overlayElement.querySelector('#root-container');
    if (rootContainer) {
      rootContainer.classList.add('not-expanded:translate-y-full', 'opacity-0');
    }
    setTimeout(() => overlayRef.dispose(), 300);
    this._promptExamplesDisplayed.set(false);
    this._promptExampleOverlay.set(null);
  }

  private createDesktopOverlay(origin: ElementRef): OverlayRef {
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(origin)
      .withPositions([
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetY: -8,
        },
      ])
      .withPush(true);

    return this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      hasBackdrop: true,
      backdropClass: 'bg-transparent',
      maxWidth: '36rem',
    });
  }

  private createMobileOverlay(): OverlayRef {
    const positionStrategy = this.overlay.position().global().centerHorizontally().bottom('0');
    const overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
    });
    this.outsideClickDispatcher.add(overlayRef);
    overlayRef.outsidePointerEvents().subscribe(() => this.closeOverlay(overlayRef));
    return overlayRef;
  }
}
